import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { defaultTo } from 'lodash';
import { toast } from 'react-toastify';

import { handleUnexpectedError } from '@/app/errors';
import { useAppForm } from '@/app/lib/tanstack-form';
import { saveNotificationPreferences, updateProfile } from '@/app/network/firefly-authenticated-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { CheckboxField, CheckboxGroup } from '@/common/components/checkbox';
import { Label } from '@/common/components/fieldset';
import { Heading, Subheading } from '@/common/components/heading';
import { Nullish } from '@/common/components/nullish';
import {
  Setting,
  SettingContent,
  SettingName,
  Settings,
  SettingsSection,
  SettingsSet
} from '@/settings/components/settings';
import { SettingsSidebar } from '@/settings/components/settings-sidebar';
import { notificationPreferenceKeys, useUserNotificationPreferences } from '@/settings/queries/notification-preferences';
import { ContentSection } from '@/shared/components/content-section';
import { Toast } from '@/shared/components/toast';
import { userKeys } from '@/shared/user/queries';
import { fullName } from '@/shared/users';

export const Route = createFileRoute('/settings/user-profile/')({
  beforeLoad: async ctx => ({
    user: await ctx.context.ensureUser()
  }),
  head: () => ({
    meta: [
      { title: 'User Profile | myTalquin' }
    ]
  }),
  component: UserProfilePage
});

function UserProfilePage() {
  const { user } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const notificationPreferences = useUserNotificationPreferences(user.id);

  const updateGeneralNotificationsMutation = useMutation({
    mutationFn: async ({ email, push, newsletter }: typeof notificationPreferencesForm.state.values) => {
      const { id: userId, first_name: firstName, last_name: lastName } = user;
      const updateProfilePromise = updateProfile(
        userId,
        firstName,
        lastName,
        navigator.language,
        newsletter
      );

      const saveNotificationPreferencesPromise = saveNotificationPreferences(userId, email, push);

      await Promise.all([updateProfilePromise, saveNotificationPreferencesPromise]);
    }
  });

  const notificationPreferencesForm = useAppForm({
    defaultValues: {
      email: defaultTo(notificationPreferences.data?.[0].selected_delivery_methods?.includes('email'), false),
      push: defaultTo(notificationPreferences.data?.[0].selected_delivery_methods?.includes('push'), false),
      newsletter: defaultTo(user.newsletter, false)
    },
    onSubmit: async ({ value }) => {
      await updateGeneralNotificationsMutation.mutateAsync(
        value,
        {
          onSuccess: async () => {
            toast.success(Toast, {
              data: {
                title: 'Notification Preferences Update',
                content: 'Your notification preferences have been updated.'
              }
            });
            await queryClient.invalidateQueries({ queryKey: userKeys.authenticated() });
            await queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.userNotificationPreferences(user.id) });
          },
          onError: error => handleUnexpectedError(error)
        }
      );
    }
  });

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <SettingsSidebar active="user-profile"/>
      <ContentSection bg="white">
        <Heading>User Profile</Heading>

        <form onSubmit={
          async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await notificationPreferencesForm.handleSubmit(event);
          }
        }
        >
          <Settings>
            <SettingsSection>
              <SettingsSet>
                <Setting>
                  <SettingName>Full Name</SettingName>
                  <SettingContent>
                    <div className="text-gray-900">{fullName(user.first_name, user.last_name)}</div>
                    <Button plain to="/settings/user-profile/change-name">Update</Button>
                  </SettingContent>
                </Setting>

                <Setting>
                  <SettingName>Email Address</SettingName>
                  <SettingContent>
                    <div className="text-gray-900">
                      <Nullish value={user.email}/>
                    </div>
                    <Button plain to="/settings/user-profile/change-email">Update</Button>
                  </SettingContent>
                </Setting>

                <Setting>
                  <SettingName>Password</SettingName>
                  <SettingContent>
                    <div className="text-gray-900">********</div>
                    <Button plain to="/settings/user-profile/change-password">Update</Button>
                  </SettingContent>
                </Setting>

                <Setting>
                  <SettingName>SMS Phone Number</SettingName>
                  <SettingContent>
                    <div className="text-gray-900">
                      <Nullish value={user.sms_phone_number_formatted} />
                    </div>
                    <Button plain to="/settings/user-profile/update-sms">Update</Button>
                  </SettingContent>
                </Setting>
              </SettingsSet>
            </SettingsSection>

            <SettingsSection>
              <Subheading>General Notifications</Subheading>

              <SettingsSet>
                <Setting>
                  <SettingName valign="top">Delivery Methods</SettingName>
                  <SettingContent>
                    <CheckboxGroup>
                      <notificationPreferencesForm.AppField name="email">
                        {field => (
                          <CheckboxField>
                            <field.FormCheckbox />
                            <Label>Email</Label>
                          </CheckboxField>
                        )}
                      </notificationPreferencesForm.AppField>

                      <notificationPreferencesForm.AppField name="push">
                        {field => (
                          <CheckboxField>
                            <field.FormCheckbox />
                            <Label>Push Notifications</Label>
                          </CheckboxField>
                        )}
                      </notificationPreferencesForm.AppField>
                    </CheckboxGroup>
                  </SettingContent>
                </Setting>

                <Setting>
                  <SettingName>Newsletter</SettingName>
                  <SettingContent>
                    <notificationPreferencesForm.AppField name="newsletter">
                      {field => (
                        <CheckboxField>
                          <field.FormCheckbox />
                          <Label>Email me <em>The Current</em></Label>
                        </CheckboxField>
                      )}
                    </notificationPreferencesForm.AppField>
                  </SettingContent>
                </Setting>
              </SettingsSet>
            </SettingsSection>
          </Settings>

          <ButtonGroup>
            <notificationPreferencesForm.Subscribe selector={state => state.isSubmitting}>
              {isSubmitting => (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              )}
            </notificationPreferencesForm.Subscribe>
          </ButtonGroup>
        </form>
      </ContentSection>
    </main>
  );
}
